/**
 * Geolocation API client for merging coordinate data
 */

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/**
 * Merge portfolio data with coordinate data
 * @param {File} file - The Excel file to process (optional, uses server file if not provided)
 * @param {boolean} createBackup - Whether to create a backup before merging
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Object>} The merge result
 */
export const mergeWithCoordinates = async (file = null, createBackup = true, onProgress = null) => {
  try {
    // Generate unique client ID for this import operation
    const clientId = `import_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Start Server-Sent Events connection for progress if callback provided
    let eventSource = null;
    if (onProgress) {
      eventSource = new EventSource(`${API_BASE_URL}/api/merge-progress/${clientId}`);
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'progress') {
            onProgress({
              stage: data.stage,
              progress: data.progress,
              current: data.current,
              total: data.total
            });
          } else if (data.type === 'complete') {
            onProgress({ stage: 'complete', progress: 100 });
            eventSource.close();
          } else if (data.type === 'error') {
            onProgress({ stage: 'error', error: data.message });
            eventSource.close();
          }
        } catch (err) {
          console.error('Error parsing SSE data:', err);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        eventSource.close();
      };
    }

    // Create FormData to send file along with other parameters
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }
    formData.append('createBackup', createBackup);
    formData.append('clientId', clientId);

    // FormData upload — let browser set Content-Type with multipart boundary.
    // Auth is via the session cookie (credentials: 'include').
    const response = await fetch(`${API_BASE_URL}/api/merge-coordinates`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      if (eventSource) {
        eventSource.close();
      }
      throw new Error(result.message || `Server responded with status: ${response.status}`);
    }

    return result;
  } catch (error) {
    console.error('Error importing coordinates:', error);
    throw error;
  }
};
